import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import { getCategoryFromPathname } from '../utils/analytics/navigation';
import { trackSentryError } from '../utils/analytics/sentry';
import { registerAsync } from '../utils/auth';
import {
  IS_LOCAL_BASE_URL,
  IS_PROD_BASE_URL,
  VITE_STRIPE_PRICING_TABLE_ID_DEV,
  VITE_STRIPE_PRICING_TABLE_ID_LOCAL,
  VITE_STRIPE_PRICING_TABLE_ID_PROD,
  VITE_STRIPE_PUBLISHABLE_KEY_DEV,
  VITE_STRIPE_PUBLISHABLE_KEY_LOCAL,
  VITE_STRIPE_PUBLISHABLE_KEY_PROD,
} from '../utils/env';
import { useFirebaseLoginState } from '../utils/hooks';
import { GradientButton } from './minicomponents/GradientButton';

const selectPricingTableId = () => {
  if (IS_LOCAL_BASE_URL) return VITE_STRIPE_PRICING_TABLE_ID_LOCAL;
  if (IS_PROD_BASE_URL) return VITE_STRIPE_PRICING_TABLE_ID_PROD;
  return VITE_STRIPE_PRICING_TABLE_ID_DEV;
};

const selectPublishableKey = () => {
  if (IS_LOCAL_BASE_URL) return VITE_STRIPE_PUBLISHABLE_KEY_LOCAL;
  if (IS_PROD_BASE_URL) return VITE_STRIPE_PUBLISHABLE_KEY_PROD;
  return VITE_STRIPE_PUBLISHABLE_KEY_DEV;
};

// Declare the custom element
declare global {
  interface HTMLElementTagNameMap {
    'stripe-pricing-table': HTMLElement;
  }
}

// Props interface using kebab-case for Stripe's custom element
interface StripePricingTableProps {
  'pricing-table-id': string;
  'publishable-key': string;
  'client-reference-id'?: string;
  'customer-email'?: string;
}

// Create a React wrapper component for the Stripe pricing table
function StripePricingTable(props: StripePricingTableProps) {
  React.useEffect(() => {
    // Ensure the custom element is defined
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return React.createElement('stripe-pricing-table', props);
}

export function PricingPage() {
  const { userId, user, isAuthStateLoading, isRegistered } = useFirebaseLoginState();
  const pricingTableId = selectPricingTableId();
  const publishableKey = selectPublishableKey();
  const location = useLocation();

  const { t } = useTranslation();

  if (isAuthStateLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isRegistered) {
    const handleLogin = async () => {
      try {
        const category = getCategoryFromPathname(location.pathname);
        await registerAsync(category);
      } catch (error) {
        trackSentryError('Failed to login in PricingPage', { error });
        console.error('Failed to login', error);
      }
    };
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          gap: 2,
        }}
      >
        <Typography>
          {t('website.pricing.signInToSubscribe', 'Please sign in to subscribe.')}
        </Typography>
        <GradientButton
          version="contained"
          onSubmitButtonHandler={handleLogin}
          buttonText={t('website.header.login', 'Login')}
        />
      </Box>
    );
  }

  return (
    <Stack spacing={4}>
      <Typography variant="h2" sx={{ textAlign: 'center', fontSize: 24 }}>
        {t('website.profile.pricingHeader')}
      </Typography>

      <StripePricingTable
        pricing-table-id={pricingTableId}
        publishable-key={publishableKey}
        client-reference-id={userId}
        customer-email={user?.email || undefined}
      />
    </Stack>
  );
}
